import { useMemo } from 'react';

import {
  useGetApiV1ExamExamItemLevels,
  useGetApiV1ExamExamItemSubTypes,
  useGetApiV1ExamLanguageDropdown,
} from '@/shared/api/generated';

/** Opção para componentes de seleção no formulário de questão */
export interface QuestionFormSelectOption {
  readonly label: string;
  readonly value: string;
}

/**
 * Hook que busca as opções dinâmicas de dificuldade, idioma e subtipo
 * dissertativo para o formulário de questão via endpoints do backend.
 *
 * @returns difficultyOptions, languageOptions, subtypeOptions e estado de loading
 *
 * @example
 * ```tsx
 * const { difficultyOptions, languageOptions, subtypeOptions } = useQuestionFormOptions();
 * ```
 */
export function useQuestionFormOptions() {
  const levelsQuery = useGetApiV1ExamExamItemLevels();
  const languagesQuery = useGetApiV1ExamLanguageDropdown();
  const subtypesQuery = useGetApiV1ExamExamItemSubTypes();

  const difficultyOptions = useMemo<QuestionFormSelectOption[]>(
    () =>
      (levelsQuery.data ?? []).map(o => ({
        label: o.value ?? '',
        value: String(o.key ?? ''),
      })),
    [levelsQuery.data],
  );

  const languageOptions = useMemo<QuestionFormSelectOption[]>(
    () =>
      (languagesQuery.data ?? []).map(o => ({
        label: o.value ?? '',
        value: o.key ?? '',
      })),
    [languagesQuery.data],
  );

  const subtypeOptions = useMemo<QuestionFormSelectOption[]>(
    () =>
      (subtypesQuery.data ?? []).map(o => ({
        label: o.value ?? '',
        value: String(o.key ?? ''),
      })),
    [subtypesQuery.data],
  );

  return {
    difficultyOptions,
    languageOptions,
    subtypeOptions,
    isLoading: levelsQuery.isPending || languagesQuery.isPending || subtypesQuery.isPending,
  } as const;
}
